import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShoppingBag, Plus, ShoppingCart, Edit, Trash2, Search, X, Upload, Loader2, Package,
} from "lucide-react";
import {
  listProducts, listStudentsForProductSale, upsertProduct, deleteProduct, createProductOrder,
} from "@/lib/api/products.functions";
import { lookupBuyerByCpf } from "@/lib/api/sales.functions";
import { PAYMENT_METHODS, isMercadoPagoMethod, isPresencialMethod, isComprovanteMethod } from "@/lib/payment-methods";
import { MercadoPagoBadge } from "@/components/shared/MercadoPagoBadge";
import { PaymentMethodsPicker } from "@/components/shared/PaymentMethodsPicker";
import { SaleWhatsappBillingModal, type SaleBillingInput } from "@/components/sales/SaleWhatsappBillingModal";
import { brIsoDate } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/m/mercadorias")({
  component: MercadoriasPage,
});

type Product = {
  id: string; name: string; description: string | null; price: number;
  stock: number | null; photo_url: string | null;
  accepted_payment_methods: string[] | null;
  delivery_lead_days?: number | null;
};
type Student = { id: string; name: string; photo_url: string | null; parent?: { name: string | null; phone: string | null; email: string | null } | null };

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

function MercadoriasPage() {
  const listFn = useServerFn(listProducts);
  const listStudentsFn = useServerFn(listStudentsForProductSale);
  const saveFn = useServerFn(upsertProduct);
  const delFn = useServerFn(deleteProduct);
  const sellFn = useServerFn(createProductOrder);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [tcId, setTcId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);

  const [saleOpen, setSaleOpen] = useState(false);
  const [saleProduct, setSaleProduct] = useState<Product | null>(null);
  const [billing, setBilling] = useState<SaleBillingInput | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([listFn(), listStudentsFn()]);
      setProducts((p.products ?? []) as any);
      setTcId(p.tcId);
      setStudents((s.students ?? []) as any);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [products, search]
  );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-primary/15 p-3 text-primary"><ShoppingBag className="h-7 w-7" /></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Mercadorias à Venda</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Cadastre produtos do CT (acessórios, bebidas, snacks) e venda diretamente para um aluno.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar mercadoria..."
              className="pl-8 w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button onClick={() => { setEditing(null); setEditOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nova Mercadoria
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={ShoppingBag} label="Itens cadastrados" value={String(products.length)} />
        <StatCard icon={Package} label="Estoque total" value={String(products.reduce((a, p) => a + (p.stock ?? 0), 0))} />
        <StatCard icon={ShoppingCart} label="Alunos do CT" value={String(students.length)} />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden animate-pulse">
              <div className="aspect-square bg-muted" />
              <CardContent className="p-4 space-y-2">
                <div className="h-4 w-3/4 bg-muted rounded" />
                <div className="h-3 w-1/2 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center gap-3 border-dashed">
          <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
          <h3 className="font-semibold">Nenhuma mercadoria cadastrada</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Comece adicionando produtos avulsos como bonés, garrafinhas, lanches e acessórios.
          </p>
          <Button onClick={() => { setEditing(null); setEditOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Nova Mercadoria
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              onEdit={() => { setEditing(p); setEditOpen(true); }}
              onDelete={async () => {
                if (!confirm(`Excluir "${p.name}"?`)) return;
                try { await delFn({ data: { id: p.id } }); toast.success("Excluído"); reload(); }
                catch (e: any) { toast.error(e?.message ?? "Erro"); }
              }}
              onSell={() => { setSaleProduct(p); setSaleOpen(true); }}
            />
          ))}
        </div>
      )}

      <ProductEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        tcId={tcId}
        onSave={async (payload) => {
          try {
            await saveFn({ data: payload });
            toast.success(payload.id ? "Atualizado" : "Cadastrado");
            setEditOpen(false);
            reload();
          } catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />

      <SellProductModal
        open={saleOpen}
        onOpenChange={setSaleOpen}
        product={saleProduct}
        students={students}
        onSell={async (payload) => {
          try {
            const res: any = await sellFn({ data: payload });
            if (payload.payment_method === "pix_comprovante" && res?.id) {
              try {
                const { sendPixSaleWhatsapp } = await import("@/lib/api/pix-proofs.functions");
                await sendPixSaleWhatsapp({ data: { kind: "product", order_id: res.id } });
                toast.success("Venda registrada e instruções de PIX enviadas no WhatsApp do responsável.");
              } catch (e: any) {
                toast.warning(`Venda registrada, mas falha ao enviar WhatsApp: ${e?.message ?? "erro"}`);
              }
            } else {
              toast.success("Venda registrada!");
            }
            setSaleOpen(false);
            reload();
            // Offer to send WhatsApp charge on the spot (somente para formas online/Mercado Pago —
            // presencial já entra no caixa e pix_comprovante já foi enviado acima via sendPixSaleWhatsapp).
            const prod = saleProduct;
            if (res?.id && prod && !isPresencialMethod(payload.payment_method) && !isComprovanteMethod(payload.payment_method)) {
              const stu = payload.student_id ? students.find((s) => s.id === payload.student_id) : undefined;
              const payerName = stu?.name ?? (payload.buyer_name || "cliente");
              const net = Math.max(0, Number(payload.amount || 0) - Number(payload.discount || 0));
              setBilling({
                id: res.id,
                kind: "product",
                amount: net,
                itemLabel: `${prod.name}${payload.quantity > 1 ? ` · ${payload.quantity}×` : ""}`,
                payerName,
                phone: payload.buyer_phone || stu?.parent?.phone || null,
                payerEmail: stu?.parent?.email ?? null,
                paymentMethod: payload.payment_method,
              });
            }
          } catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />

      <SaleWhatsappBillingModal
        open={!!billing}
        onOpenChange={(o) => !o && setBilling(null)}
        data={billing}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground truncate">{label}</div>
          <div className="text-xl font-bold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductCard({
  product, onEdit, onDelete, onSell,
}: { product: Product; onEdit: () => void; onDelete: () => void; onSell: () => void }) {
  const stockLow = (product.stock ?? 0) <= 5;
  return (
    <Card className="overflow-hidden group hover:shadow-lg hover:border-primary/40 transition-all">
      <div className="aspect-square bg-gradient-to-br from-muted via-background to-muted/40 relative overflow-hidden">
        {product.photo_url ? (
          <img src={product.photo_url} alt={product.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ShoppingBag className="h-20 w-20 text-muted-foreground/25" />
          </div>
        )}
        <button
          type="button"
          onClick={onSell}
          title="Vender para um aluno"
          className="absolute bottom-3 right-3 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <ShoppingCart className="h-5 w-5" />
        </button>
        <div className="absolute top-3 right-3 flex gap-1">
          <button onClick={onEdit} className="h-8 w-8 rounded-md bg-background/90 backdrop-blur flex items-center justify-center hover:bg-background border" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="h-8 w-8 rounded-md bg-background/90 backdrop-blur flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground border" title="Excluir">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        {stockLow && (
          <Badge variant="destructive" className="absolute top-3 left-3">Estoque baixo</Badge>
        )}
      </div>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold leading-tight line-clamp-2">{product.name}</h3>
            {product.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{product.description}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="font-bold text-primary">{fmtMoney(Number(product.price))}</div>
            <div className={`text-[11px] ${stockLow ? "text-destructive" : "text-muted-foreground"}`}>
              Estoque: {product.stock ?? 0}
            </div>
          </div>
        </div>
        <Button variant="outline" className="w-full" onClick={onSell}>
          <ShoppingCart className="h-4 w-4 mr-2" /> Vender
        </Button>
      </CardContent>
    </Card>
  );
}

function ProductEditModal({
  open, onOpenChange, editing, tcId, onSave,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Product | null; tcId: string | null;
  onSave: (p: any) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState<string>("0");
  const [stock, setStock] = useState<string>("0");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [acceptedMethods, setAcceptedMethods] = useState<string[]>([]);
  const [leadDays, setLeadDays] = useState<string>("0");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? "");
      setPrice(String(editing.price ?? 0));
      setStock(String(editing.stock ?? 0));
      setPhotoUrl(editing.photo_url);
      setAcceptedMethods(editing.accepted_payment_methods ?? []);
      setLeadDays(String(editing.delivery_lead_days ?? 0));
    } else {
      setName(""); setDescription(""); setPrice("0"); setStock("0"); setPhotoUrl(null);
      setAcceptedMethods(PAYMENT_METHODS.map((m) => m.value));
      setLeadDays("0");
    }
  }, [open, editing]);

  const handlePhotoUpload = async (file: File) => {
    if (!tcId) { toast.error("Cadastre o CT primeiro"); return; }
    try {
      setUploading(true);
      const ext = (file.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `${tcId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("uniforms").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data, error: sErr } = await supabase.storage.from("uniforms").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (sErr) throw sErr;
      setPhotoUrl(data.signedUrl);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro no upload");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      await onSave({
        id: editing?.id ?? null,
        name: name.trim(),
        description: description.trim() || null,
        price: Number(price) || 0,
        stock: parseInt(stock || "0", 10) || 0,
        photo_url: photoUrl,
        accepted_payment_methods: acceptedMethods,
        delivery_lead_days: Math.max(0, parseInt(leadDays || "0", 10) || 0),
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Mercadoria" : "Nova Mercadoria"}</DialogTitle>
          <DialogDescription>
            Cadastre produtos avulsos do seu CT. Sem grade de tamanhos — venda por unidade.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
          <div className="space-y-2">
            <Label>Foto</Label>
            <div className="aspect-square rounded-lg border-2 border-dashed bg-muted/30 relative flex items-center justify-center overflow-hidden">
              {photoUrl ? (
                <>
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPhotoUrl(null)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-background/90 border flex items-center justify-center"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
              )}
            </div>
            <label className="block">
              <input type="file" accept="image/*" className="hidden" disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); e.target.value = ""; }} />
              <span className="inline-flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Enviando..." : "Enviar foto"}
              </span>
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Nome da Mercadoria *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Garrafa Squeeze CT" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalhes do produto (opcional)" rows={2} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Preço (R$) *</Label>
                <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
              <div>
                <Label>Estoque</Label>
                <Input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
              </div>
              <div>
                <Label>Prazo de Entrega (dias)</Label>
                <Input type="number" min="0" value={leadDays} onChange={(e) => setLeadDays(e.target.value)} placeholder="0" />
                <p className="text-[11px] text-muted-foreground mt-1">Base para calcular a previsão em vendas com entrega futura.</p>
              </div>
            </div>

            <PaymentMethodsPicker value={acceptedMethods} onChange={setAcceptedMethods} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar Mercadoria
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SellProductModal({
  open, onOpenChange, product, students, onSell,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  product: Product | null; students: Student[];
  onSell: (p: any) => Promise<void>;
}) {
  const allowedMethods = useMemo(() => {
    const accepted = product?.accepted_payment_methods ?? [];
    return accepted.length > 0
      ? PAYMENT_METHODS.filter((m) => accepted.includes(m.value))
      : PAYMENT_METHODS;
  }, [product]);

  const [studentId, setStudentId] = useState<string>("");
  const [walkIn, setWalkIn] = useState<boolean>(false);
  const [buyerName, setBuyerName] = useState<string>("");
  const [buyerCpf, setBuyerCpf] = useState<string>("");
  const [buyerPhone, setBuyerPhone] = useState<string>("");
  const [cpfLookupMsg, setCpfLookupMsg] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("1");
  const [amount, setAmount] = useState<string>("0");
  const [discount, setDiscount] = useState<string>("0");
  const [paymentMethod, setPaymentMethod] = useState<string>("pix");
  const [dueDate, setDueDate] = useState<string>("");
  const [deliveryType, setDeliveryType] = useState<"imediata" | "futura">("imediata");
  const [expectedDelivery, setExpectedDelivery] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const lookupCpfFn = useServerFn(lookupBuyerByCpf);

  const computeExpected = (days: number) => {
    return brIsoDate(new Date(Date.now() + (days || 0) * 86400000));
  };

  useEffect(() => {
    if (!open || !product) return;
    setStudentId(""); setWalkIn(false); setBuyerName(""); setBuyerCpf(""); setBuyerPhone(""); setCpfLookupMsg("");
    setQuantity("1");
    setAmount(String(product.price ?? 0));
    setDiscount("0");
    setPaymentMethod(allowedMethods[0]?.value ?? "pix");
    setDueDate(""); setSearch("");
    setDeliveryType("imediata");
    setExpectedDelivery(computeExpected(product.delivery_lead_days ?? 0));
  }, [open, product, allowedMethods]);

  useEffect(() => {
    if (!product) return;
    if (deliveryType === "futura" && !expectedDelivery) {
      setExpectedDelivery(computeExpected(product.delivery_lead_days ?? 0));
    }
  }, [deliveryType, product]);

  // CPF lookup (debounced)
  useEffect(() => {
    const digits = buyerCpf.replace(/\D/g, "");
    if (!walkIn || digits.length !== 11) { setCpfLookupMsg(""); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r: any = await lookupCpfFn({ data: { cpf: digits } });
        if (cancel) return;
        if (r?.student) {
          setStudentId(r.student.id);
          setWalkIn(false);
          setBuyerName(r.student.name);
          setCpfLookupMsg(`✓ Vinculado ao aluno ${r.student.name}`);
        } else if (r?.parent) {
          setBuyerName(r.parent.name);
          setCpfLookupMsg(`✓ Responsável encontrado: ${r.parent.name}`);
        } else {
          setCpfLookupMsg("CPF não encontrado no cadastro.");
        }
      } catch { /* ignore */ }
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [buyerCpf, walkIn]);


  const filteredStudents = useMemo(
    () => students.filter((s) => s.name.toLowerCase().includes(search.toLowerCase())),
    [students, search]
  );

  if (!product) return null;

  const handleSell = async () => {
    if (!walkIn && !studentId) { toast.error("Escolha um aluno ou marque venda avulsa"); return; }
    if (walkIn && !buyerName.trim()) { toast.error("Informe o nome do comprador"); return; }
    if (deliveryType === "futura" && !expectedDelivery) { toast.error("Informe a data prevista de entrega"); return; }
    const qty = Math.max(1, parseInt(quantity || "1", 10));
    setSaving(true);
    try {
      await onSell({
        product_id: product.id,
        student_id: walkIn ? null : studentId,
        buyer_name: walkIn ? buyerName.trim() : null,
        buyer_cpf: walkIn ? (buyerCpf.replace(/\D/g, "") || null) : null,
        buyer_phone: walkIn ? (buyerPhone.replace(/\D/g, "") || null) : null,
        quantity: qty,
        amount: Number(amount) || 0,
        discount: Number(discount) || 0,
        payment_method: paymentMethod,
        due_date: dueDate || null,
        status: "pago",
        delivery_type: deliveryType,
        expected_delivery_date: deliveryType === "futura" ? expectedDelivery : null,
      });
      setConfirmOpen(false);
    } finally { setSaving(false); }
  };


  const selectedStudent = students.find((s) => s.id === studentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Vender mercadoria</DialogTitle>
          <DialogDescription>Registre a venda para um aluno do seu Centro de Treinamento.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/20 p-3 flex items-center gap-3">
          <div className="h-16 w-16 rounded-md bg-muted overflow-hidden flex items-center justify-center shrink-0">
            {product.photo_url ? <img src={product.photo_url} alt="" className="w-full h-full object-cover" /> : <ShoppingBag className="h-8 w-8 text-muted-foreground/40" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{product.name}</div>
            {product.description && (
              <div className="text-xs text-muted-foreground truncate">{product.description}</div>
            )}
          </div>
          <div className="font-bold text-primary">{fmtMoney(Number(product.price))}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={walkIn}
                onChange={(e) => { setWalkIn(e.target.checked); if (e.target.checked) setStudentId(""); }}
              />
              <span>Venda avulsa (sem aluno cadastrado)</span>
            </label>

            {walkIn ? (
              <div className="space-y-2">
                <div>
                  <Label>Nome do comprador *</Label>
                  <Input
                    placeholder="Ex.: João da Silva"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>CPF (opcional)</Label>
                  <Input
                    placeholder="Somente números"
                    value={buyerCpf}
                    onChange={(e) => setBuyerCpf(e.target.value.replace(/\D/g, "").slice(0, 11))}
                  />
                  {cpfLookupMsg && (
                    <p className="text-[11px] mt-1 text-muted-foreground">{cpfLookupMsg}</p>
                  )}
                </div>
                <div>
                  <Label>WhatsApp (opcional)</Label>
                  <Input
                    placeholder="DDD + número (para envio de cobrança)"
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label>Aluno *</Label>
                {selectedStudent ? (
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <div className="h-8 w-8 rounded-full bg-muted overflow-hidden flex items-center justify-center">
                      {selectedStudent.photo_url ? <img src={selectedStudent.photo_url} alt="" className="w-full h-full object-cover" /> : selectedStudent.name[0]}
                    </div>
                    <div className="flex-1 font-medium">{selectedStudent.name}</div>
                    <Button variant="ghost" size="sm" onClick={() => setStudentId("")}>Trocar</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input className="pl-8" placeholder="Buscar aluno..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <div className="max-h-40 overflow-y-auto rounded-md border divide-y">
                      {filteredStudents.length === 0 ? (
                        <div className="text-xs text-muted-foreground p-3 text-center">Nenhum aluno encontrado</div>
                      ) : filteredStudents.map((s) => (
                        <button key={s.id} type="button" onClick={() => setStudentId(s.id)}
                          className="w-full text-left p-2 hover:bg-muted flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-muted overflow-hidden flex items-center justify-center text-xs">
                            {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : s.name[0]}
                          </div>
                          <span className="text-sm">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Quantidade *</Label>
            <Input type="number" min="1" value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                const qty = Math.max(1, parseInt(e.target.value || "1", 10));
                setAmount(String((Number(product.price) || 0) * qty));
              }} />
          </div>

          <div>
            <Label>Valor Total (R$) *</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>

          <div>
            <Label>Desconto (R$)</Label>
            <Input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            <p className="text-[11px] text-muted-foreground mt-1">
              Total: <strong>{fmtMoney(Math.max(0, (Number(amount)||0) - (Number(discount)||0)))}</strong>
            </p>
          </div>

          <div>
            <Label>Forma de Pagamento</Label>
            <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {allowedMethods.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="flex items-center gap-1.5">
                      {m.label}
                      {isMercadoPagoMethod(m.value) && <MercadoPagoBadge />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 rounded-md bg-muted/30 border p-2 text-xs text-muted-foreground">
            {isPresencialMethod(paymentMethod)
              ? "💵 Recebimento presencial — entra direto no Caixa como pago."
              : isComprovanteMethod(paymentMethod)
              ? "📲 Será enviada a chave PIX no WhatsApp do responsável. Confirmar via comprovante."
              : isMercadoPagoMethod(paymentMethod)
              ? "🔵 Cobrança gerada via Mercado Pago — confirmação automática após pagamento."
              : "⏳ Esta venda ficará pendente até ser confirmada em Confirmação de Pagamentos."}
          </div>

          <div>
            <Label>Entrega *</Label>
            <Select value={deliveryType} onValueChange={(v) => setDeliveryType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="imediata">Imediata (entregar agora)</SelectItem>
                <SelectItem value="futura">Entrega futura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {deliveryType === "futura" && (
            <div>
              <Label>Previsão de entrega *</Label>
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              if (!walkIn && !studentId) { toast.error("Escolha um aluno ou marque venda avulsa"); return; }
              if (walkIn && !buyerName.trim()) { toast.error("Informe o nome do comprador"); return; }
              setConfirmOpen(true);
            }}
            disabled={saving}
          >
            <ShoppingCart className="h-4 w-4 mr-2" /> Vender
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase tracking-wide">CONFIRMAR TIPO DE ENTREGA</AlertDialogTitle>
            <AlertDialogDescription>
              Esta venda será registrada como <strong>{deliveryType === "imediata" ? "ENTREGA IMEDIATA" : "ENTREGA FUTURA"}</strong>
              {deliveryType === "futura" && expectedDelivery ? ` prevista para ${new Date(expectedDelivery + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button type="button" variant={deliveryType === "imediata" ? "default" : "outline"} className="flex-1" onClick={() => setDeliveryType("imediata")}>Imediata</Button>
            <Button type="button" variant={deliveryType === "futura" ? "default" : "outline"} className="flex-1" onClick={() => setDeliveryType("futura")}>Futura</Button>
          </div>
          {deliveryType === "futura" && (
            <div>
              <Label>Previsão de entrega</Label>
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSell} disabled={saving || (deliveryType === "futura" && !expectedDelivery)}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirmar venda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
